import { COMPANY_COLORS } from '@/constants/colors.constant'
import { CardTitleLookup } from '@/constants/ReportDashboard/cards-metadata'
import { formatAssetClass } from '@/constants/ReportDashboard/asset-class'
import { getReportItemMetadata, ReportItem } from '@/constants/ReportDashboard/report-items'
import {
  useAttributionDateRange,
  useAttributionLoading,
  useAttributionPortfolioName,
  useAttributionSecurityContribution,
  useAttributionSecurityError,
} from '@/contexts/FAAttributionDataContext/hooks'
import { useAttributionHoldingsControls } from '@/contexts/AttributionHoldingsContext'
import { useDarkModeCheck, useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { IGfpaSecurityContrib } from '@/interfaces/gfpaReport.interface'
import { AttributionHoldingsPrefs, cardPrefsContextKey, CardProps } from '@/interfaces/raDashboard.interface'
import { putUserPreference } from '@/services/userPreference.service'
import { getHeader } from '@/utils/factorAnalytics.util'
import { convertNumericDateToString } from '@/utils/dateUtils'
import dayjs from 'dayjs'
import {
  columnsSideBarPanelDef,
  filtersSideBarPanelDef,
  getGridThemeClass,
  sideBarDefaultConfig,
  sortColumnGroupsByName,
} from '@/utils/grid-utils/ag-grid-config'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import {
  ColDef,
  ColGroupDef,
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnRowGroupChangedEvent,
  ColumnState,
  GridReadyEvent,
  IAggFuncParams,
  IRowNode,
  ITooltipParams,
  ModuleRegistry,
  SelectionChangedEvent,
  SideBarDef,
  ToolPanelVisibleChangedEvent,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { CsvExportModule } from '@ag-grid-community/csv-export'
import { AgGridReact } from '@ag-grid-community/react'
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel'
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import { MenuModule } from '@ag-grid-enterprise/menu'
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { cardStyle } from '../../../styles/reportDashboardStyles'

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  RowGroupingModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MenuModule,
  CsvExportModule,
  ExcelExportModule,
])

const NUMERIC_CELL = 'ag-right-aligned-cell'

// Attribution values: plain 2dp, unit (bps / %) in the header, real zeros blanked (factors that
// don't apply to a security).
const numberFormatter = (p: ValueFormatterParams<IGfpaSecurityContrib, number | null>): string =>
  p.value == null || Number(p.value) === 0 ? '' : Number(p.value).toFixed(2)

// Security-detail values: format off the ReportItem metadata precision, keeping zeros. The unit
// (% / bps / currency) lives in the header, so values carry no unit suffix.
const detailFormatter = (item: ReportItem) => {
  const meta = getReportItemMetadata(item)
  const precision = meta?.displayPrecision ?? 2
  return (p: ValueFormatterParams<IGfpaSecurityContrib, number | null>): string => {
    if (p.value == null) return ''
    return Number(p.value).toLocaleString(navigator.language, {
      minimumFractionDigits: precision,
      maximumFractionDigits: precision,
    })
  }
}

// Hover shows the full-precision value (cells round to 2dp, which can read as 0 for tiny numbers).
const fullValueTooltip = (p: ITooltipParams<IGfpaSecurityContrib, number | null>): string =>
  p.value == null ? '' : String(p.value)

// Group aggregation for the % return columns: notional-weighted average, Σ(weight × return) / Σ(weight),
// weighted by each security's % Notional. Walks the group's filtered leaves so it reconciles with the
// visible rows (and any active column filter), like the bps columns' sum.
const notionalWeightedAvg = (params: IAggFuncParams<IGfpaSecurityContrib, number | null>): number | null => {
  const field = params.colDef.field as keyof IGfpaSecurityContrib | undefined
  if (!field) return null
  let weightSum = 0
  let weightedSum = 0
  const visit = (node: IRowNode<IGfpaSecurityContrib>) => {
    if (node.group) {
      ;(node.childrenAfterFilter ?? []).forEach(visit)
      return
    }
    const data = node.data
    const value = data?.[field]
    const weight = Number(data?.[ReportItem.PctFaceMarketValue] ?? 0)
    if (data == null || value == null || !Number.isFinite(weight)) return
    weightSum += weight
    weightedSum += weight * Number(value)
  }
  ;(params.rowNode.childrenAfterFilter ?? []).forEach(visit)
  return weightSum === 0 ? null : weightedSum / weightSum
}

// Field + label + display format. The two `pct` (%) columns are notional-weighted on grouping; the
// rest are bps contributions that plain-sum.
type ReturnCol = { field: keyof IGfpaSecurityContrib; label: string; pct?: boolean }
const RETURN_COLUMNS: ReturnCol[] = [
  { field: 'security_ret_contrib', label: 'Security Return Contribution' },
  { field: 'security_ret', label: 'Security Return', pct: true },
  { field: 'model_ret', label: 'Model Return', pct: true },
  { field: 'fi_yield_ret_contrib', label: 'Yield Return Contribution' },
  { field: 'fi_d1_ret_contrib', label: 'Yield Curve Return Contribution' },
  { field: 'fi_sector_ret_contrib', label: 'Sector Return Contribution' },
  { field: 'fi_quality_ret_contrib', label: 'Quality Return Contribution' },
  { field: 'fi_other_ret_contrib', label: 'Other Spread Return Contribution' },
  { field: 'eq_country_ret_contrib', label: 'Country Return Contribution' },
  { field: 'eq_region_ret_contrib', label: 'Region Return Contribution' },
  { field: 'eq_industry_ret_contrib', label: 'Industry Return Contribution' },
  { field: 'eq_fundamental_ret_contrib', label: 'Style Return Contribution' },
  { field: 'commodity_ret_contrib', label: 'Commodity Return Contribution' },
  { field: 'alternatives_ret_contrib', label: 'Alternative Return Contribution' },
  { field: 'model_ret_contrib', label: 'Model Return Contribution' },
  { field: 'currency_ret_contrib', label: 'Currency Return Contribution' },
  { field: 'hedge_cost_contrib', label: 'Hedge Cost Return Contribution' },
  { field: 'interaction_contrib', label: 'Interaction Return Contribution' },
  { field: 'selection_contrib', label: 'Selection Return Contribution' },
]

// Security-detail columns — hidden by default, available via the columns sidebar and draggable to group.
const SECURITY_DETAIL_FIELDS: ReportItem[] = [
  ReportItem.Holdings,
  ReportItem.SectorName,
  ReportItem.CountryCode,
  ReportItem.Currency,
  ReportItem.Price,
  ReportItem.PctFaceMarketValue,
  ReportItem.NotionalValue,
  ReportItem.PctMarketValue,
  ReportItem.SecurityCount,
  ReportItem.AssetType,
  ReportItem.AssetClass,
  ReportItem.CountryofIncorp,
  ReportItem.CountryofRisk,
  ReportItem.BaseCurrencyName,
  ReportItem.CUSIP9,
  ReportItem.ExchangeTicker,
  ReportItem.ISIN12,
  ReportItem.SEDOL7,
]

// Equity + Fixed Income security-level attributes — hidden by default, available via the columns
// sidebar and draggable to group.
const EQUITY_FIELDS: ReportItem[] = [
  ReportItem.DividendYield,
  ReportItem.Beta,
  ReportItem.BookToPrice,
  ReportItem.EPRatio,
  ReportItem.EarningsPerShare,
  ReportItem.MarketCap,
  ReportItem.MarketCapLog,
  ReportItem.Momentum,
  ReportItem.PBRatio,
  ReportItem.PERatio,
  ReportItem.TrailingEPS,
  ReportItem.Volatility,
  ReportItem.MSCICountry,
  ReportItem.GICSIndustryGrp,
  ReportItem.GICSIndustry,
  ReportItem.GICSSector,
  ReportItem.GICSSubIndustry,
  ReportItem.ReturnOnEquity,
]
const FIXED_INCOME_FIELDS: ReportItem[] = [
  ReportItem.CouponRate,
  ReportItem.MaturityDate,
  ReportItem.MarketValue,
  ReportItem.EffectiveDuration,
  ReportItem.MoodyQuality,
  ReportItem.YearsToMaturity,
  ReportItem.OAS,
  ReportItem.DV01,
  ReportItem.Convexity,
  ReportItem.YieldToMaturity,
  ReportItem.SpreadDuration,
  ReportItem.YieldToWorst,
  ReportItem.AverageLife,
  ReportItem.BookValue,
  ReportItem.CashFlowYield,
  ReportItem.CurrentYield,
  ReportItem.EffectiveD2,
  ReportItem.EffectiveD3,
  ReportItem.DurContributionPct,
  ReportItem.DurContributionYrs,
  ReportItem.EffectiveSpreadDuration,
  ReportItem.SpreadDurContributionPct,
  ReportItem.SpreadDurContributionYrs,
  ReportItem.GainLoss,
  ReportItem.MacaulayDuration,
  ReportItem.MarketValuePar,
  ReportItem.ModifiedDuration,
  ReportItem.SPQuality,
  ReportItem.BaseTotalAccruedInterest,
  ReportItem.YearsToWorst,
  ReportItem.DatedDate,
  ReportItem.KeyRate1Month,
  ReportItem.KeyRate1Year,
  ReportItem.KeyRate2Year,
  ReportItem.KeyRate3Month,
  ReportItem.KeyRate3Year,
  ReportItem.KeyRate4Year,
  ReportItem.KeyRate5Year,
  ReportItem.KeyRate6Month,
  ReportItem.KeyRate6Year,
  ReportItem.KeyRate7Year,
  ReportItem.KeyRate8Year,
  ReportItem.KeyRate9Month,
  ReportItem.KeyRate9Year,
  ReportItem.KeyRate10Year,
  ReportItem.KeyRate15Year,
  ReportItem.KeyRate0Year,
  ReportItem.KeyRate25Year,
  ReportItem.KeyRate30Year,
]

// Date fields — formatted to YYYY-MM-DD (the endpoint returns ISO datetimes) and groupable by date.
const DATE_FIELDS = new Set<ReportItem>([ReportItem.MaturityDate, ReportItem.DatedDate])
// Non-numeric (groupable) fields across the security/EQ/FI sets — text codes and ratings. Everything not
// listed here or in DATE_FIELDS is treated as numeric and formatted off the ReportItem metadata.
const CATEGORICAL_FIELDS = new Set<ReportItem>([
  ReportItem.SectorName,
  ReportItem.CountryCode,
  ReportItem.Currency,
  ReportItem.AssetType,
  ReportItem.AssetClass,
  ReportItem.CountryofIncorp,
  ReportItem.CountryofRisk,
  ReportItem.BaseCurrencyName,
  ReportItem.CUSIP9,
  ReportItem.ExchangeTicker,
  ReportItem.ISIN12,
  ReportItem.SEDOL7,
  ReportItem.MSCICountry,
  ReportItem.GICSIndustryGrp,
  ReportItem.GICSIndustry,
  ReportItem.GICSSector,
  ReportItem.GICSSubIndustry,
  ReportItem.ReturnOnEquity,
  ReportItem.MoodyQuality,
  ReportItem.SPQuality,
])

// Normalize a date to YYYY-MM-DD across the shapes this family of endpoints uses: 8-digit YYYYMMDD
// numbers and ISO datetimes. Unparseable values pass through unchanged.
const formatDate = (v: string | number | null | undefined): string => {
  if (v == null || v === '') return ''
  const s = String(v)
  if (/^\d{8}$/.test(s)) return convertNumericDateToString(s)
  const d = dayjs(s)
  return d.isValid() ? d.format('YYYY-MM-DD') : s
}
const dateValueFormatter = (p: ValueFormatterParams<IGfpaSecurityContrib, string | number | null>): string =>
  formatDate(p.value)

// A hidden, sidebar-available security/EQ/FI attribute column: dates format to YYYY-MM-DD (groupable),
// text codes group, numeric values format off the ReportItem metadata.
const detailColumn = (f: ReportItem): ColDef<IGfpaSecurityContrib> => {
  const base = { field: f as keyof IGfpaSecurityContrib, headerName: getHeader(f), minWidth: 120, hide: true }
  // cellDataType is pinned so the right filter/type is used even when the first row's value is null.
  if (DATE_FIELDS.has(f)) return { ...base, cellDataType: 'text', enableRowGroup: true, valueFormatter: dateValueFormatter, tooltipValueGetter: fullValueTooltip }
  if (f === ReportItem.AssetClass) return { ...base, cellDataType: 'text', enableRowGroup: true, valueFormatter: (p) => formatAssetClass(p.value) }
  if (CATEGORICAL_FIELDS.has(f)) return { ...base, cellDataType: 'text', enableRowGroup: true }
  return { ...base, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: detailFormatter(f), tooltipValueGetter: fullValueTooltip }
}

// Sidebar column layout — groups the columns panel into "Security Details" and "Attribution", like risk.
const TOOLS_COLUMN_LAYOUT = sortColumnGroupsByName<IGfpaSecurityContrib>([
  {
    headerName: 'Security Details',
    children: [
      { field: ReportItem.StandardID, headerName: getHeader(ReportItem.StandardID) },
      { field: ReportItem.Description, headerName: getHeader(ReportItem.Description) },
      ...SECURITY_DETAIL_FIELDS.map((f) => ({ field: f as keyof IGfpaSecurityContrib, headerName: getHeader(f) })),
    ],
  },
  {
    headerName: 'Attribution',
    children: RETURN_COLUMNS.map((c) => ({ field: c.field, headerName: `${c.label} (${c.pct ? '%' : 'bps'})` })),
  },
  {
    headerName: 'Equity',
    children: EQUITY_FIELDS.map((f) => ({ field: f as keyof IGfpaSecurityContrib, headerName: getHeader(f) })),
  },
  {
    headerName: 'Fixed Income',
    children: FIXED_INCOME_FIELDS.map((f) => ({ field: f as keyof IGfpaSecurityContrib, headerName: getHeader(f) })),
  },
])

// Column-selection + filter sidebar (right), matching the risk Holdings grid.
const HOLDINGS_SIDEBAR: SideBarDef = {
  ...sideBarDefaultConfig,
  toolPanels: [
    {
      ...columnsSideBarPanelDef,
      toolPanelParams: {
        ...columnsSideBarPanelDef.toolPanelParams,
        suppressSyncLayoutWithGrid: true,
        columnLayout: TOOLS_COLUMN_LAYOUT,
      },
    },
    filtersSideBarPanelDef,
  ],
}

const EMPTY_ROWS: IGfpaSecurityContrib[] = []

const HoldingsTableCard: React.FC<CardProps> = (props) => {
  const { cardId } = props
  const theme = useTheme()
  const isDarkMode = useDarkModeCheck()
  const title = CardTitleLookup[cardId]
  const rows = useAttributionSecurityContribution() ?? EMPTY_ROWS
  const { highlightSecurities, clearHighlightedSecurities, setHighlightGroups } = useAttributionHoldingsControls()
  const gridRef = useRef<AgGridReact<IGfpaSecurityContrib>>(null)

  // Stable refs so save/apply callbacks never need to be recreated
  const cardPrefsRef = useRef(props.cardPrefs)
  const onCardPrefsChangeRef = useRef(props.onCardPrefsChange)
  useEffect(() => {
    cardPrefsRef.current = props.cardPrefs
  }, [props.cardPrefs])
  useEffect(() => {
    onCardPrefsChangeRef.current = props.onCardPrefsChange
  }, [props.onCardPrefsChange])

  // Track which dashboard we've already applied prefs for so saves don't re-trigger apply
  const initializedForDashboard = useRef<string | null>(null)
  // Suppress save events that fire async from applyColumnState / setFilterModel
  const applyingRef = useRef(false)

  const applyGridPrefs = useCallback((prefs: AttributionHoldingsPrefs) => {
    const api = gridRef.current?.api
    if (!api) return
    applyingRef.current = true
    api.getToolPanelInstance('columns')?.setColumnLayout(TOOLS_COLUMN_LAYOUT)
    if (prefs.columnState) {
      api.applyColumnState({ state: prefs.columnState as unknown as ColumnState[], applyOrder: true })
    }
    if (prefs.filterModel) {
      api.setFilterModel(prefs.filterModel)
    }
    // Defer the reset so async events from applyColumnState are still suppressed
    setTimeout(() => {
      applyingRef.current = false
    }, 0)
  }, [])

  const onGridReady = useCallback(
    (e: GridReadyEvent) => {
      // Group the columns panel on every load, not only when saved prefs exist.
      e.api.getToolPanelInstance('columns')?.setColumnLayout(TOOLS_COLUMN_LAYOUT)
      const prefs = cardPrefsRef.current
      const saved = prefs?.attributionHoldings
      if (saved) {
        initializedForDashboard.current = prefs.dashboardName ?? null
        applyGridPrefs(saved)
      }
    },
    [applyGridPrefs],
  )

  // Apply only when the dashboard changes (not on every save)
  useEffect(() => {
    const dashboardName = props.cardPrefs?.dashboardName
    const saved = props.cardPrefs?.attributionHoldings
    if (saved && gridRef.current?.api && dashboardName !== initializedForDashboard.current) {
      initializedForDashboard.current = dashboardName ?? null
      applyGridPrefs(saved)
    }
  }, [props.cardPrefs, applyGridPrefs])

  const saveGridState = useCallback(() => {
    if (applyingRef.current) return
    const api = gridRef.current?.api
    const prefs = cardPrefsRef.current
    if (!api || !prefs?.dashboardName) return
    const newPrefs: AttributionHoldingsPrefs = {
      columnState: api.getColumnState() as unknown as Record<string, unknown>[],
      filterModel: api.getFilterModel() as Record<string, unknown>,
    }
    const updated = { ...prefs, attributionHoldings: newPrefs }
    onCardPrefsChangeRef.current?.(updated)
    putUserPreference(cardPrefsContextKey(prefs.dashboardName), updated)
  }, [])

  const onColumnMoved = useCallback(
    (e: ColumnMovedEvent) => {
      if (e.finished) saveGridState()
    },
    [saveGridState],
  )

  const onColumnResized = useCallback(
    (e: ColumnResizedEvent) => {
      if (e.finished) saveGridState()
    },
    [saveGridState],
  )

  const onToolPanelVisibleChanged = useCallback(
    (e: ToolPanelVisibleChangedEvent) => {
      if (!e.visible) saveGridState()
    },
    [saveGridState],
  )

  const onColumnRowGroupChanged = useCallback(
    (_e: ColumnRowGroupChangedEvent<IGfpaSecurityContrib>) => {
      clearHighlightedSecurities()
      saveGridState()
    },
    [saveGridState, clearHighlightedSecurities],
  )

  // Selecting a group row narrows the Top/Bottom card and the bubble chart to that group's securities.
  // Collect the selected groups' leaf identifiers, and build the group-path labels (running each group
  // column's own valueFormatter so labels match the displayed text, e.g. "Fixed Income").
  const onRowSelectionChanged = useCallback(
    (e: SelectionChangedEvent<IGfpaSecurityContrib>) => {
      const selectedNodes = e.api.getSelectedNodes()
      if (selectedNodes.length === 0) {
        clearHighlightedSecurities()
        return
      }
      const ids = selectedNodes.flatMap(
        (node) =>
          node.allLeafChildren?.map((row) => row.data?.[ReportItem.StandardID]).filter((v): v is string => v != null) ?? [],
      )
      const groupings: string[] = []
      let currNode: IRowNode<IGfpaSecurityContrib> | null = selectedNodes[0]
      while (currNode?.key) {
        let groupingName = currNode.key
        const nodeCol = currNode.rowGroupColumn
        const nodeColDef = nodeCol?.getColDef()
        const valFormatter = nodeColDef?.valueFormatter
        if (typeof valFormatter === 'function' && nodeColDef != null && nodeCol != null) {
          groupingName = valFormatter({
            value: currNode.key,
            node: currNode,
            column: nodeCol,
            colDef: nodeColDef,
            context: nodeColDef.context,
            api: e.api,
            data: {},
          })
        }
        groupings.unshift(groupingName)
        currNode = currNode.parent
      }
      highlightSecurities(ids)
      setHighlightGroups(...groupings)
    },
    [clearHighlightedSecurities, highlightSecurities, setHighlightGroups],
  )

  const columnDefs = useMemo<(ColDef<IGfpaSecurityContrib> | ColGroupDef<IGfpaSecurityContrib>)[]>(
    () => [
      {
        field: ReportItem.StandardID,
        headerName: getHeader(ReportItem.StandardID),
        pinned: 'left',
        minWidth: 130,
        enableRowGroup: true,
      },
      {
        field: ReportItem.Description,
        headerName: getHeader(ReportItem.Description),
        minWidth: 180,
        enableRowGroup: true,
      },
      ...SECURITY_DETAIL_FIELDS.map(detailColumn),
      // The two %-weighted returns stay standalone (their own unit); grouped rows show the
      // notional-weighted average.
      ...RETURN_COLUMNS.filter((c) => c.pct).map<ColDef<IGfpaSecurityContrib>>((c) => ({
        field: c.field,
        headerName: `${c.label} (%)`,
        minWidth: 150,
        cellClass: NUMERIC_CELL,
        valueFormatter: numberFormatter,
        tooltipValueGetter: fullValueTooltip,
        aggFunc: notionalWeightedAvg,
      })),
      // The bps contributions share a two-tier "Return Contribution (bps)" header; each child just
      // shows its factor name, so the repeated "Return Contribution (bps)" collapses into the parent.
      {
        headerName: 'Return Contribution (bps)',
        children: RETURN_COLUMNS.filter((c) => !c.pct).map<ColDef<IGfpaSecurityContrib>>((c) => ({
          field: c.field,
          headerName: c.label.replace(/ Return Contribution$/, ''),
          minWidth: 110,
          cellClass: NUMERIC_CELL,
          valueFormatter: numberFormatter,
          tooltipValueGetter: fullValueTooltip,
          aggFunc: 'sum',
        })),
      },
      // Equity + Fixed Income security attributes — hidden by default, available via the columns sidebar.
      ...EQUITY_FIELDS.map(detailColumn),
      ...FIXED_INCOME_FIELDS.map(detailColumn),
    ],
    [],
  )

  const defaultColDef = useMemo<ColDef<IGfpaSecurityContrib>>(
    // enableRowGroup is set per column (categorical only) — grouping by a continuous value would make
    // roughly one group per security.
    () => ({ sortable: true, resizable: true, filter: true }),
    [],
  )

  const autoGroupColumnDef = useMemo<ColDef<IGfpaSecurityContrib>>(
    () => ({ headerName: 'Group', minWidth: 200, pinned: 'left', checkboxSelection: ({ node }) => !!node.group }),
    [],
  )

  const loading = useAttributionLoading()
  const error = useAttributionSecurityError()
  const portfolioName = useAttributionPortfolioName()
  const dateRange = useAttributionDateRange()

  // Export filename: portfolioName_holdings_beginDate_endDate — the _holdings tag keeps it distinct from
  // the Factor Attribution export, which shares the same portfolio + range.
  const exportFileBase = useMemo(() => {
    const name = (portfolioName?.trim() || 'portfolio').replace(/[\\/:*?"<>|]/g, '_')
    const range = dateRange ? `_${dateRange.beginDate.format('YYYYMMDD')}_${dateRange.endDate.format('YYYYMMDD')}` : ''
    return `${name}_holdings${range}`
  }, [portfolioName, dateRange])
  // Export the raw full-precision value, not the 2dp display format; date fields export as YYYY-MM-DD.
  const rawValueCallback = useCallback((params: any) => {
    const field = params?.column?.getColDef?.()?.field
    const data = params?.node?.data
    const raw = field && data && data[field] !== undefined ? data[field] : params.value
    if (field && DATE_FIELDS.has(field as ReportItem)) return formatDate(raw)
    return raw
  }, [])
  // Decided filename + raw full-precision values; columnGroups keeps the grouped header tier (e.g. bps).
  // CSV is flat, so skip group/footer rows — their aggregates would double-count a plain sum over a column.
  const defaultCsvExportParams = useMemo(
    () => ({
      fileName: `${exportFileBase}.csv`,
      processCellCallback: rawValueCallback,
      columnGroups: true,
      shouldRowBeSkipped: (p: any) => !!p.node?.group || !!p.node?.footer,
    }),
    [exportFileBase, rawValueCallback],
  )
  // Excel keeps the collapsible row-group outline, so parent rows stay (no double-count when collapsed).
  const defaultExcelExportParams = useMemo(
    () => ({ fileName: `${exportFileBase}.xlsx`, processCellCallback: rawValueCallback, columnGroups: true }),
    [exportFileBase, rawValueCallback],
  )
  // Right-click menu: Export only (copy intentionally omitted for this card).
  const getContextMenuItems = useCallback(() => ['export'], [])

  return (
    <Stack
      sx={{
        ...cardStyle,
        width: '100%',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
        padding: '12px',
        boxSizing: 'border-box',
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
      }}
    >
      <Stack sx={{ mb: '10px' }}>
        <Typography variant='subtitle2' sx={{ fontWeight: 700, color: theme.textPrimary }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: '14px', color: theme.textSecondary }}>
          Security-level returns and contribution to portfolio return
        </Typography>
      </Stack>

      {error ? (
        <Stack sx={{ flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 600, color: COMPANY_COLORS.ERROR }}>
            Couldn&apos;t load holdings
          </Typography>
          <Typography sx={{ fontSize: '13px', color: theme.textSecondary, mt: '4px' }}>
            There was an error fetching the data. Please retry, or reach out to support if it persists.
          </Typography>
        </Stack>
      ) : loading && rows.length === 0 ? (
        <Stack sx={{ flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={28} sx={{ color: COMPANY_COLORS.DARK_BLUE }} />
        </Stack>
      ) : rows.length === 0 ? (
        <Stack sx={{ flex: 1, minHeight: 0, alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: '13px', color: theme.textSecondary }}>
            Run analysis for the selected portfolio and date range to view holdings.
          </Typography>
        </Stack>
      ) : (
        <Box
          className={getGridThemeClass(isDarkMode)}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            '--ag-secondary-foreground-color': theme.textSecondary,
            '--ag-control-panel-background-color': theme.cardBackground,
            '--ag-input-border-color': theme.border,
            '--ag-grid-size': '4px',
            // Center the "Return Contribution (bps)" group header. The label is flex:none + sticky
            // (pinned) so stretch it to allow centering.
            '& .ag-header-group-cell-label': { justifyContent: 'center' },
            '& .ag-header-group-cell-label.ag-sticky-label': { flex: '1 1 auto' },
            '& .ag-header-group-text': { textAlign: 'center' },
          }}
        >
          <AgGridReact<IGfpaSecurityContrib>
            ref={gridRef}
            containerStyle={{ height: '100%', width: '100%' }}
            rowData={rows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            autoGroupColumnDef={autoGroupColumnDef}
            rowSelection='multiple'
            suppressRowClickSelection
            rowGroupPanelShow='always'
            suppressRowGroupHidesColumns
            sideBar={HOLDINGS_SIDEBAR}
            getContextMenuItems={getContextMenuItems}
            defaultCsvExportParams={defaultCsvExportParams}
            defaultExcelExportParams={defaultExcelExportParams}
            suppressAggFuncInHeader
            columnHoverHighlight
            enableBrowserTooltips
            onGridReady={onGridReady}
            onColumnRowGroupChanged={onColumnRowGroupChanged}
            onSelectionChanged={onRowSelectionChanged}
            onSortChanged={saveGridState}
            onColumnMoved={onColumnMoved}
            onColumnPinned={saveGridState}
            onColumnResized={onColumnResized}
            onToolPanelVisibleChanged={onToolPanelVisibleChanged}
          />
        </Box>
      )}
    </Stack>
  )
}

export default HoldingsTableCard
